// ============================================
// services/authService.js
// ============================================
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { ERRORS } from '../utils/constants';

// Test-only helpers for Cypress runs
const isE2E = () => typeof window !== 'undefined' && !!window.Cypress;
const E2E_STORAGE_KEY = 'e2eLoggedIn';
const setE2EUser = (user) => {
  try {
    if (isE2E()) {
      window.localStorage.setItem(E2E_STORAGE_KEY, JSON.stringify(user));
      // Notify same-tab listeners
      window.dispatchEvent(new CustomEvent('e2e-auth-change'));
    }
  } catch (_) {}
};
const clearE2EUser = () => {
  try {
    if (isE2E()) {
      window.localStorage.removeItem(E2E_STORAGE_KEY);
      // Notify same-tab listeners
      window.dispatchEvent(new CustomEvent('e2e-auth-change'));
    }
  } catch (_) {}
};

const mapAuthError = (error) => {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Invalid email or password';
    case 'auth/user-not-found':
      return 'No account found with this email';
    case 'auth/too-many-requests':
      return ERRORS.TOO_MANY_REQUESTS;
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/email-already-in-use':
      return 'Email is already registered';
    case 'auth/weak-password':
      return 'Password is too weak';
    default:
      return 'Invalid email or password';
  }
};

async function ensureUserProfile(uid, data) {
  const ref = doc(db, 'users', uid);
  const existing = await getDoc(ref);
  if (!existing.exists()) {
    // Fresh create: include required fields per security rules
    await setDoc(ref, {
      firstName: (data.firstName || '').trim(),
      lastName: (data.lastName || '').trim(),
      email: data.email || '',
      createdAt: serverTimestamp(),
      // Keep legacy token arrays for compatibility
      deviceTokens: [],
      fcmTokens: [],
    });
    return;
  }

  // Document exists (likely created by Cloud Function onUserCreated) —
  // if names are blank, backfill them from the signup form.
  const curr = existing.data() || {};
  const updates = {};
  const newFirst = (data.firstName || '').trim();
  const newLast = (data.lastName || '').trim();
  if (newFirst && !((curr.firstName || '').trim())) updates.firstName = newFirst;
  if (newLast && !((curr.lastName || '').trim())) updates.lastName = newLast;
  if (Object.keys(updates).length > 0) {
    await updateDoc(ref, updates);
  }
}

async function login(email, password) {
  try {
    // Cypress E2E: simulate auth without real Firebase
    if (isE2E()) {
      // Simulate network latency for UI loading states
      await new Promise((r) => setTimeout(r, 600));
      // Simple rate limiting: lock after 5 failed attempts in this browser
      const FAIL_KEY = 'e2eLoginFailCount';
      let failCount = 0;
      try { failCount = parseInt(window.localStorage.getItem(FAIL_KEY) || '0', 10) || 0; } catch (_) {}
      if (email === 'test@example.com' && password === 'password123') {
        const user = { uid: 'e2e-user', email };
        setE2EUser(user);
        try { window.localStorage.setItem(FAIL_KEY, '0'); } catch (_) {}
        return { success: true, user };
      }

      // Failed login: increment and determine error message
      const newCount = failCount + 1;
      try { window.localStorage.setItem(FAIL_KEY, String(newCount)); } catch (_) {}
      if (newCount >= 5) {
        return { success: false, error: ERRORS.TOO_MANY_REQUESTS };
      }
      return { success: false, error: 'Invalid email or password' };
    }
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const { uid, email: userEmail } = cred.user || {};
    return { success: true, user: { uid, email: userEmail } };
  } catch (e) {
    return { success: false, error: mapAuthError(e) };
  }
}

async function signup(email, password, firstName, lastName) {
  try {
    // Cypress E2E: simulate success path
    if (isE2E()) {
      await new Promise((r) => setTimeout(r, 600));
      const user = { uid: 'e2e-user', email };
      setE2EUser(user);
      return { success: true, user };
    }
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const user = cred.user;
    try {
      await updateProfile(user, { displayName: `${firstName} ${lastName}`.trim() });
    } catch (_) {}
    await ensureUserProfile(user.uid, { firstName, lastName, email });
    // Extra safeguard: perform a final merge write so names are present
    // even if the user doc existed prior to this call.
    try {
      await setDoc(doc(db, 'users', user.uid), {
        firstName: (firstName || '').trim(),
        lastName: (lastName || '').trim(),
      }, { merge: true });
    } catch (_) {}
    try { await sendEmailVerification(user); } catch (_) {}
    return { success: true, user: { uid: user.uid, email: user.email } };
  } catch (e) {
    return { success: false, error: mapAuthError(e) };
  }
}

async function resetPassword(email) {
  try {
    if (isE2E()) {
      return { success: true };
    }
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (e) {
    return { success: false, error: mapAuthError(e) };
  }
}

async function logout() {
  try {
    if (isE2E()) {
      clearE2EUser();
      return { success: true };
    }
    await signOut(auth);
    return { success: true };
  } catch (e) {
    return { success: false, error: 'Failed to logout' };
  }
}

async function resendVerificationEmail() {
  try {
    if (isE2E()) {
      return { success: true };
    }
    const user = auth?.currentUser;
    if (!user) {
      return { success: false, error: ERRORS.GENERIC_ERROR };
    }
    // Build actionCodeSettings to route back to the app. In tests/SSR, omit.
    const isBrowser = typeof window !== 'undefined';
    const actionCodeSettings = isBrowser
      ? {
          url: `${window.location.origin}/`,
          handleCodeInApp: true,
        }
      : undefined;

    await sendEmailVerification(user, actionCodeSettings);
    return { success: true };
  } catch (e) {
    return { success: false, error: mapAuthError(e) };
  }
}

export const authService = { login, signup, resetPassword, logout, resendVerificationEmail };